'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { fetchApi } from '@/lib/api'

export interface UserProfile {
  id: number
  username: string
  email: string
  fullName: string | null
  avatar: string | null
  status: 'active' | 'inactive' | 'banned'
  lastLoginAt: string | null
  lastLoginIp: string | null
  roles: string[]
  permissions: string[]
  isSuperAdmin: boolean
}

interface AuthContextType {
  user: UserProfile | null
  token: string | null
  isLoading: boolean
  login: (uid: string, pass: string) => Promise<void>
  logout: () => Promise<void>
  hasPermission: (perm: string) => boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  hasPermission: () => false,
  refreshUser: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = async () => {
    try {
      const res = await fetchApi('/auth/me')
      if (res.status === 'success') {
        setUser(res.data)
        localStorage.setItem('cms_user', JSON.stringify(res.data))
      }
    } catch {
      setUser(null)
      setToken(null)
      localStorage.removeItem('cms_token')
      localStorage.removeItem('cms_user')
    }
  }

  useEffect(() => {
    const savedToken = localStorage.getItem('cms_token')
    const savedUser = localStorage.getItem('cms_user')

    if (savedToken) {
      setToken(savedToken)
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser))
        } catch {
          // Ignore parse error
        }
      }
      refreshUser().finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [])

  const login = async (uid: string, password: string) => {
    const res = await fetchApi('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ uid, password }),
    })

    if (res.status === 'success') {
      const newToken = res.data.token
      const userData = res.data.user
      setToken(newToken)
      setUser(userData)
      localStorage.setItem('cms_token', newToken)
      localStorage.setItem('cms_user', JSON.stringify(userData))
    }
  }

  const logout = async () => {
    try {
      await fetchApi('/auth/logout', { method: 'POST' })
    } catch {
      // Ignore network errors on logout
    } finally {
      setUser(null)
      setToken(null)
      localStorage.removeItem('cms_token')
      localStorage.removeItem('cms_user')
      window.location.href = '/login'
    }
  }

  const hasPermission = (perm: string): boolean => {
    if (isLoading) return true
    if (!user) return false
    if (user.isSuperAdmin || user.permissions.includes('*')) return true
    return user.permissions.includes(perm)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        logout,
        hasPermission,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
